/**
 * Thruster trails: camera-facing additive ribbons built from each thruster's
 * recent positions. Width and brightness scale with speed, so the trail
 * itself reads as a speedometer — a faint wisp when hovering, a long bright
 * streak under boost/pulse.
 *
 * The flight path is COMPRESSED into the ribbon rather than truncated: each
 * fixed-rate history step contributes at most an equal share of the visible
 * length. At speed the real path is kilometers long and a 1:1 rendering
 * shows only a near-straight sliver of it — compression keeps the full
 * ~1.6 s of heading history on screen, so turns render as genuine curves at
 * any speed, with no bend-amplification step to magnify sampling noise.
 *
 * History is kept in SYSTEM space (float64) and converted to render space
 * per frame, so floating-origin rebases never kink the ribbon. Additive
 * blending fades to black along the tail — no alpha sorting issues.
 */
import * as THREE from "three";
import type { ThrusterTip } from "../ship/parts";

/** The body's INTERPOLATED render transform (planet.group) — the frame the terrain draws
 *  in. When anchored, history is stored planet-LOCAL and record+rebuild both use this pose,
 *  so they cancel exactly and the ribbon welds to the ground (no sub-frame jitter). */
export interface TrailAnchor {
  gpos: THREE.Vector3;
  gquat: THREE.Quaternion;
  /** Carry strength 0..1; > 0.5 ⇒ store history planet-LOCAL and ride the body. */
  k: number;
}

const POINTS = 96;
/** History samples are promoted at a fixed 60 Hz so the trail always spans
 *  ~POINTS/60 s of flight regardless of render frame rate. */
const SAMPLE_DT = 1 / 60;

class Ribbon {
  mesh: THREE.Mesh;
  /** Circular-ish history, newest first; system-space float64. */
  history: THREE.Vector3[] = [];
  /** Time accrued toward promoting the live head into a fixed sample. */
  private acc = 0;
  private positions: THREE.BufferAttribute;
  private colors: THREE.BufferAttribute;
  /** Scratch buffers (render-space raw, pre-smoothed, resampled points). */
  private raw = Array.from({ length: POINTS }, () => new THREE.Vector3());
  private pre = Array.from({ length: POINTS }, () => new THREE.Vector3());
  private sm = Array.from({ length: POINTS }, () => new THREE.Vector3());
  /** Cumulative arc length along the pre-smoothed polyline. */
  private cum = new Float32Array(POINTS);
  /** Per-point distance from the camera to the nearest adjacent SEGMENT. */
  private prox = new Float32Array(POINTS);
  /** History re-expressed in render space (see rebuild). */
  private rh = Array.from({ length: POINTS }, () => new THREE.Vector3());

  constructor(scene: THREE.Scene, material: THREE.Material) {
    const geo = new THREE.BufferGeometry();
    this.positions = new THREE.BufferAttribute(new Float32Array(POINTS * 2 * 3), 3);
    // RGBA (4-component) so the tail can fade via ALPHA under normal blending —
    // the ribbon is OPAQUE at the head, not an additive see-through glow.
    this.colors = new THREE.BufferAttribute(new Float32Array(POINTS * 2 * 4), 4);
    geo.setAttribute("position", this.positions);
    geo.setAttribute("color", this.colors);
    const index: number[] = [];
    for (let i = 0; i < POINTS - 1; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    geo.setIndex(index);
    this.mesh = new THREE.Mesh(geo, material);
    // Draw AFTER the cloud decks/volume (renderOrder 4.0–4.8) so the bright
    // exhaust isn't washed transparent where it passes over a cloud — clouds
    // don't write depth, so draw order (not depth) decides the overlap. Kept
    // slightly above the atmosphere shell (5), and still depthTest'd so terrain occludes.
    this.mesh.renderOrder = 5.1;
    this.mesh.frustumCulled = false; // spans up to ~400 m under pulse
    scene.add(this.mesh);
  }

  /**
   * history[0] is a LIVE head that tracks the thruster tip every frame; it
   * is frozen into a permanent sample on a fixed 60 Hz cadence, so history
   * depth in seconds doesn't change with render frame rate.
   */
  push(sysPos: THREE.Vector3, dt: number): void {
    if (this.history.length === 0) {
      this.history.unshift(sysPos.clone());
      this.acc = 0;
      return;
    }
    this.history[0].copy(sysPos);
    this.acc += dt;
    if (this.acc >= SAMPLE_DT) {
      this.acc = Math.min(this.acc - SAMPLE_DT, SAMPLE_DT);
      this.history.unshift(sysPos.clone());
      if (this.history.length > POINTS) this.history.pop();
    }
  }

  rebuild(
    origin: THREE.Vector3,
    camPos: THREE.Vector3,
    base: THREE.Color,
    speed: number,
    /** Max visible trail length in world units (meters). */
    maxLen: number,
    /** Re-anchor the carried history to the body's interpolated render pose (or null in deep space). */
    anchor: TrailAnchor | null,
  ): void {
    const n = this.history.length;
    if (n === 0) return;
    const pos = this.positions.array as Float32Array;
    const col = this.colors.array as Float32Array;
    // Negative (reverse) speed kills the trail entirely — a thruster plume
    // ahead of a backing-up ship reads as a glitch, not exhaust.
    const brightness = Math.min(1, Math.max(0, speed) / 80);
    // Width is CONSTANT — speed expresses itself as trail LENGTH (and
    // brightness), never as width; wide ribbons read as flat bars.
    const width0 = 0.2;

    // Pass 1: compress the flight path into the visible window. March back
    // from the thruster tip along the recorded per-sample displacements,
    // capping each step at an equal share of maxLen. At low speed the steps
    // are under the cap, so the ribbon IS the true path; at speed the steps
    // compress, keeping the entire heading history on screen — that is what
    // makes turns render as real curves at 3 km/s instead of a straight
    // sliver of a kilometers-long arc.
    // History → render space before compressing. When ANCHORED the history is stored
    // planet-LOCAL, so project it through the body's INTERPOLATED pose (the exact frame the
    // terrain draws in): `render = gquat·local + gpos`. This is the SAME pose used to record
    // it (see ThrusterTrails.update), so the head round-trips to the live thruster and older
    // samples ride the ground — welded, no sub-frame jitter. Deep space is plain absolute.
    for (let i = 0; i < n; i++) {
      if (anchor === null) {
        this.rh[i].copy(this.history[i]).sub(origin);
      } else {
        this.rh[i].copy(this.history[i]).applyQuaternion(anchor.gquat).add(anchor.gpos);
      }
    }

    const step = maxLen / (POINTS - 1);
    this.raw[0].copy(this.rh[0]);
    for (let i = 1; i < POINTS; i++) {
      const newer = this.rh[Math.min(i - 1, n - 1)];
      const older = this.rh[Math.min(i, n - 1)];
      const dx = older.x - newer.x;
      const dy = older.y - newer.y;
      const dz = older.z - newer.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const p = this.raw[i - 1];
      if (d < 1e-9) {
        this.raw[i].copy(p);
      } else {
        const s = Math.min(d, step) / d;
        this.raw[i].set(p.x + dx * s, p.y + dy * s, p.z + dz * s);
      }
    }

    // Pass 2: neighbor-average so per-sample jitter and steering steps don't
    // read as kinks in the ribbon. The HEAD is preserved exactly — averaging
    // it would drag the ribbon's root behind the thruster tip.
    this.pre[0].copy(this.raw[0]);
    for (let i = 1; i < POINTS; i++) {
      const a = this.raw[i - 1];
      const b = this.raw[i];
      const c = this.raw[Math.min(POINTS - 1, i + 1)];
      this.pre[i]
        .copy(b)
        .multiplyScalar(2)
        .add(a)
        .add(c)
        .multiplyScalar(0.25);
    }

    // Pass 3: cumulative arc length (compression already bounds it: capped
    // steps sum to at most maxLen).
    this.cum[0] = 0;
    for (let i = 1; i < POINTS; i++) {
      this.cum[i] = this.cum[i - 1] + this.pre[i].distanceTo(this.pre[i - 1]);
    }
    const last = POINTS - 1;
    const total = this.cum[last];

    // Pass 4: resample to POINTS vertices spaced UNIFORMLY along the path,
    // positions from a Catmull-Rom through the samples — even vertex
    // distribution for the width/fade ramps, smooth between 60 Hz samples.
    if (last === 0 || total < 1e-6) {
      for (let j = 0; j < POINTS; j++) this.sm[j].copy(this.pre[0]);
    } else {
      let k = 0;
      for (let j = 0; j < POINTS; j++) {
        const tj = (total * j) / (POINTS - 1);
        while (k < last - 1 && this.cum[k + 1] < tj) k++;
        const segLen = this.cum[k + 1] - this.cum[k];
        const u = segLen > 1e-9 ? (tj - this.cum[k]) / segLen : 0;
        const p0 = this.pre[Math.max(0, k - 1)];
        const p1 = this.pre[k];
        const p2 = this.pre[k + 1];
        const p3 = this.pre[Math.min(last, k + 2)];
        const u2 = u * u;
        const u3 = u2 * u;
        this.sm[j].set(
          0.5 * (2 * p1.x + (p2.x - p0.x) * u + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * u2 + (3 * p1.x - p0.x - 3 * p2.x + p3.x) * u3),
          0.5 * (2 * p1.y + (p2.y - p0.y) * u + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * u2 + (3 * p1.y - p0.y - 3 * p2.y + p3.y) * u3),
          0.5 * (2 * p1.z + (p2.z - p0.z) * u + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * u2 + (3 * p1.z - p0.z - 3 * p2.z + p3.z) * u3),
        );
      }
    }

    // Camera proximity per SEGMENT (not per vertex): at speed the sample
    // spacing far exceeds the fade radius, so a segment can graze the
    // camera while both endpoints are distant — fading only vertices left
    // that one segment rendering as a giant flat bar across the screen.
    this.prox.fill(Infinity);
    for (let i = 0; i < POINTS - 1; i++) {
      const a = this.sm[i];
      const b = this.sm[i + 1];
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const abz = b.z - a.z;
      const apx = camPos.x - a.x;
      const apy = camPos.y - a.y;
      const apz = camPos.z - a.z;
      const len2 = abx * abx + aby * aby + abz * abz;
      let t = len2 > 1e-9 ? (apx * abx + apy * aby + apz * abz) / len2 : 0;
      t = Math.max(0, Math.min(1, t));
      const dx = a.x + abx * t - camPos.x;
      const dy = a.y + aby * t - camPos.y;
      const dz = a.z + abz * t - camPos.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d < this.prox[i]) this.prox[i] = d;
      if (d < this.prox[i + 1]) this.prox[i + 1] = d;
    }

    const seg = new THREE.Vector3();
    const view = new THREE.Vector3();
    const side = new THREE.Vector3();
    let lastSeg = new THREE.Vector3(0, 0, 1);

    for (let i = 0; i < POINTS; i++) {
      const p = this.sm[i];
      // Central-difference tangent: stable through direction changes. On a
      // degenerate (duplicate-sample) segment, reuse the previous tangent
      // instead of snapping to an arbitrary axis — no flickering side vectors.
      seg.copy(this.sm[Math.max(0, i - 1)]).sub(this.sm[Math.min(POINTS - 1, i + 1)]);
      if (seg.lengthSq() < 1e-10) seg.copy(lastSeg);
      else lastSeg.copy(seg);
      view.copy(camPos).sub(p);
      side.crossVectors(view, seg);
      const sl = side.length();
      if (sl < 1e-6) side.set(1, 0, 0);
      else side.divideScalar(sl);

      // Vertices are uniform in arc length after resampling, so the index
      // IS the normalized distance along the ribbon.
      const t = i / (POINTS - 1);
      const w = width0 * Math.pow(1 - t, 0.85);
      side.multiplyScalar(w);

      const o = i * 6;
      pos[o] = p.x - side.x;
      pos[o + 1] = p.y - side.y;
      pos[o + 2] = p.z - side.z;
      pos[o + 3] = p.x + side.x;
      pos[o + 4] = p.y + side.y;
      pos[o + 5] = p.z + side.z;

      // Near-camera attenuation: ONLY suppress a segment that is essentially
      // clipping into the lens (its camera-facing quad would smear across the
      // screen). The radius is deliberately tiny — the chase camera sits only
      // ~6–11 m behind the thrusters, so a large fade band (the old 6→34 m)
      // erased the whole near half of the ribbon and read as "zones" where the
      // trail vanished. Fully gone under ~1.5 m, fully opaque by ~5.5 m.
      const nearFade = Math.min(1, Math.max(0, (this.prox[i] - 1.5) / 4));
      // OPAQUE along the body: alpha holds at 1 and only ramps down over the
      // last ~35% of the tail, so the ribbon reads solid instead of a
      // transparent wash. `brightness` gates the whole streak in as the ship
      // gets moving; nearFade prevents a flat bar across the lens.
      const tail = Math.min(1, (1 - t) / 0.35);
      const alpha = Math.min(1, tail * brightness * nearFade);
      const c = i * 8;
      col[c] = col[c + 4] = base.r;
      col[c + 1] = col[c + 5] = base.g;
      col[c + 2] = col[c + 6] = base.b;
      col[c + 3] = col[c + 7] = alpha;
    }
    this.positions.needsUpdate = true;
    this.colors.needsUpdate = true;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.mesh);
    this.mesh.geometry.dispose();
  }
}

export class ThrusterTrails {
  private ribbons: Ribbon[] = [];
  private tips: ThrusterTip[] = [];
  private material: THREE.MeshBasicMaterial;
  private tmp = new THREE.Vector3();
  /** Whether history is currently stored planet-LOCAL (vs absolute system space). */
  private anchored = false;
  private _invGquat = new THREE.Quaternion();
  private _loc = new THREE.Vector3();
  /** Smoothed max trail length in world units (see update). */
  private maxLen = 6;

  constructor(private scene: THREE.Scene) {
    this.material = new THREE.MeshBasicMaterial({
      vertexColors: true,
      blending: THREE.NormalBlending,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }

  /** (Re)assign thruster exhaust points (ship-local). Call after a refit. */
  /**
   * Carry the recorded exhaust history with a rotating/orbiting body's
   * frame (same transform the ship itself received) — otherwise ribbons
   * smear with the planet's absolute sweep instead of trailing the hull.
   */
  carryFrame(prevCenter: THREE.Vector3, dq: THREE.Quaternion, delta: THREE.Vector3, k: number): void {
    if (this.anchored) return; // planet-LOCAL history already rides the body — don't double-carry
    if (k <= 0) return;
    for (const r of this.ribbons) {
      for (const h of r.history) {
        h.sub(prevCenter).applyQuaternion(dq).add(prevCenter).addScaledVector(delta, k);
      }
    }
  }

  setTips(tips: ThrusterTip[]): void {
    for (const r of this.ribbons) r.dispose(this.scene);
    this.ribbons = tips.map(() => new Ribbon(this.scene, this.material));
    this.tips = tips.map((t) => ({ offset: t.offset.clone(), color: t.color.clone() }));
  }

  /**
   * Per frame: sample thruster positions and rebuild ribbon geometry.
   * `speed` is SIGNED forward speed — reversing hides the trail.
   */
  update(
    sysPos: THREE.Vector3,
    quat: THREE.Quaternion,
    speed: number,
    origin: THREE.Vector3,
    camPos: THREE.Vector3,
    dt: number,
    /** Interp-pose anchor of the body the ship rides (null in deep space). */
    anchor: TrailAnchor | null = null,
  ): void {
    // Storage frame: within a body's carry (k > 0.5) we keep history planet-LOCAL so it rides
    // the ground exactly; far out it's absolute system space. On a switch between the two, drop
    // history (its samples are in the other frame). Local storage makes carryFrame a no-op.
    const a = anchor !== null && anchor.k > 0.5 ? anchor : null;
    if ((a !== null) !== this.anchored) {
      this.resetHistory();
      this.anchored = a !== null;
    }
    if (a !== null) this._invGquat.copy(a.gquat).invert();

    // Visible ribbon length: ramps with speed and caps — the flight path is
    // COMPRESSED into this window (see Ribbon.rebuild), so the length never
    // needs to chase distance travelled to keep the curve on screen. Pulse
    // gets a stretch for scale, but stays well under a frame of travel.
    const fwd = Math.max(0, speed);
    const pulseK = Math.min(1, Math.max(0, (fwd - 3000) / 20000));
    const target = Math.max(6, Math.min(150, fwd * 0.25) + 250 * pulseK);
    this.maxLen += (target - this.maxLen) * (1 - Math.exp(-8 * dt));

    for (let i = 0; i < this.ribbons.length; i++) {
      this.tmp.copy(this.tips[i].offset).applyQuaternion(quat).add(sysPos); // thruster, SYSTEM space
      if (a !== null) {
        // → planet-LOCAL (interp centre = gpos + origin) so the sample rides the body.
        this._loc.copy(this.tmp).sub(a.gpos).sub(origin).applyQuaternion(this._invGquat);
        this.ribbons[i].push(this._loc, dt);
      } else {
        this.ribbons[i].push(this.tmp, dt);
      }
      this.ribbons[i].rebuild(origin, camPos, this.tips[i].color, speed, this.maxLen, a);
    }
  }

  setVisible(visible: boolean): void {
    for (const r of this.ribbons) r.mesh.visible = visible;
  }

  /** Drop all history (after a warp — old points are in another galaxy). */
  resetHistory(): void {
    for (const r of this.ribbons) r.history.length = 0;
  }

  dispose(): void {
    for (const r of this.ribbons) r.dispose(this.scene);
    this.ribbons = [];
    this.material.dispose();
  }
}
